// Stimulus controller for handling nested dropdown menu logic in Calibrate Curricular Action Items
import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="nested-dropdown"

export default class extends Controller {
  // Define Stimulus targets for main menu, submenus, and form
  static targets = ["mainMenu", "submenu", "form"];

  connect() {
    // Disable all checkboxes on initial load
    this.disableAllCheckboxes();
    // Set up event listeners for dropdown menu interactions
    // (No longer open submenu on focus of any menu item)
    document.addEventListener(
      "click",
      (this.handleDocumentClickBound = this.handleDocumentClick.bind(this))
    );
    this.element.addEventListener(
      "change",
      (this.handleCheckboxChangeBound = this.handleCheckboxChange.bind(this))
    );
    this.element.addEventListener(
      "keydown",
      (this.handleCheckboxKeydownBound = this.handleCheckboxKeydown.bind(this))
    );

    // Add Escape key handler for main menu button (Select Assessments)
    const mainButton = this.element.querySelector(
      ".nested-dropdown__toggle[data-action*='toggleMain']"
    );
    if (mainButton) {
      this.handleMainButtonKeydownBound =
        this.handleMainButtonKeydown.bind(this);
      mainButton.addEventListener("keydown", this.handleMainButtonKeydownBound);
    }

    // On load, sync group checkboxes for correct indeterminate/checked state
    this.syncAllGroups();

    // Listen for custom event dispatched from results partial to re-enable checkboxes
    this.handleResultsUpdatedBound = () => {
      this.enableAllCheckboxes();
    };
    document.addEventListener(
      "results-displayed-in-ui",
      this.handleResultsUpdatedBound
    );
  }

  disconnect() {
    // Clean up event listeners when controller disconnects
    document.removeEventListener("click", this.handleDocumentClickBound);
    this.element.removeEventListener("change", this.handleCheckboxChangeBound);
    this.element.removeEventListener(
      "keydown",
      this.handleCheckboxKeydownBound
    );
    // Remove Escape key handler for main menu button
    const mainButton = this.element.querySelector(
      ".nested-dropdown__toggle[data-action*='toggleMain']"
    );
    if (mainButton && this.handleMainButtonKeydownBound) {
      mainButton.removeEventListener(
        "keydown",
        this.handleMainButtonKeydownBound
      );
    }

    // Remove custom event listener
    if (this.handleResultsUpdatedBound) {
      document.removeEventListener(
        "results-displayed-in-ui",
        this.handleResultsUpdatedBound
      );
    }
  }

  // (Unused) Handler for menu item focus, kept for reference
  handleMenuItemFocus(event) {
    // Open the submenu for the focused menu item, if it exists
    const menuItem = event.currentTarget.closest(".nested-dropdown__item");
    if (!menuItem) return;
    // Close all other submenus first
    this.closeAllSubmenus();
    const submenu = menuItem.querySelector(
      "[data-nested-dropdown-target='submenu']"
    );
    if (submenu) {
      submenu.classList.remove("hidden");
      // Optionally, add hover effect to the focused menu item
      menuItem.classList.add("hover");
    }
  }

  // Handles Escape key on the main menu button to close all menus
  handleMainButtonKeydown(event) {
    if (event.key === "Escape") {
      // Close the main menu and all submenus
      if (
        this.mainMenuTarget &&
        !this.mainMenuTarget.classList.contains("hidden")
      ) {
        this.mainMenuTarget.classList.add("hidden");
      }
      this.closeAllSubmenus();
      event.preventDefault();
    }
  }

  // Toggle all EARLY checkboxes in all submenus from All Calibrate (top-level)
  toggleAllEarly(event) {
    const checked = event.target.checked;
    const allEarlyCheckboxes = this.element.querySelectorAll(
      "input[type='checkbox'][data-group][data-group$='_early']"
    );
    allEarlyCheckboxes.forEach((cb) => {
      cb.checked = checked;
    });
    this.updateHiddenField();
    this.syncAllGroups();
    this.syncAllCalibrateIndeterminate();
    this.debouncedSubmit();
  }

  // Toggle all LATE checkboxes in all submenus from All Calibrate (top-level)
  toggleAllLate(event) {
    const checked = event.target.checked;
    const allLateCheckboxes = this.element.querySelectorAll(
      "input[type='checkbox'][data-group][data-group$='_late']"
    );
    allLateCheckboxes.forEach((cb) => {
      cb.checked = checked;
    });
    this.updateHiddenField();
    this.syncAllGroups();
    this.syncAllCalibrateIndeterminate();
    this.debouncedSubmit();
  }

  // Sync group checkboxes (indeterminate/checked state) for each groupbox
  syncAllGroups() {
    const groupboxes = this.element.querySelectorAll(
      "input[type='checkbox'][data-groupbox='true']"
    );
    groupboxes.forEach((groupbox) => {
      const group = groupbox.dataset.group;
      if (!group) return;
      const children = this.element.querySelectorAll(
        `input[type='checkbox'][data-group='${group}']:not([data-groupbox='true'])`
      );
      const checkedCount = Array.from(children).filter(
        (cb) => cb.checked
      ).length;
      const indeterminateCount = Array.from(children).filter(
        (cb) => cb.indeterminate
      ).length;
      if (
        children.length &&
        checkedCount === children.length &&
        indeterminateCount === 0
      ) {
        groupbox.checked = true;
        groupbox.indeterminate = false;
      } else if (
        children.length &&
        checkedCount === 0 &&
        indeterminateCount === 0
      ) {
        groupbox.checked = false;
        groupbox.indeterminate = false;
      } else {
        groupbox.checked = false;
        groupbox.indeterminate = true;
      }
    });
    this.syncAllCalibrateIndeterminate();
  }

  // Set indeterminate state for All Calibrate Early/Late checkboxes (top-level)
  syncAllCalibrateIndeterminate() {
    // EARLY
    const allEarly = this.element.querySelector(
      "input[data-all-calibrate-early]"
    );

    const groupboxesEarly = allEarly
      ? this.element.querySelectorAll(
          "input[type='checkbox'][data-groupbox='true'][data-group$='_early']"
        )
      : [];
    const checkedCount = Array.from(groupboxesEarly).filter(
      (cb) => cb.checked
    ).length;
    const indeterminateCount = Array.from(groupboxesEarly).filter(
      (cb) => cb.indeterminate
    ).length;

    if (allEarly) {
      if (checkedCount === groupboxesEarly.length && indeterminateCount === 0) {
        allEarly.checked = true;
        allEarly.indeterminate = false;
      } else if (checkedCount === 0 && indeterminateCount === 0) {
        allEarly.checked = false;
        allEarly.indeterminate = false;
      } else {
        allEarly.checked = false;
        allEarly.indeterminate = true;
      }
    }

    // LATE
    const allLate = this.element.querySelector(
      "input[data-all-calibrate-late]"
    );

    const groupboxesLate = allLate
      ? this.element.querySelectorAll(
          "input[type='checkbox'][data-groupbox='true'][data-group$='_late']"
        )
      : [];
    const checkedCountLate = Array.from(groupboxesLate).filter(
      (cb) => cb.checked
    ).length;
    const indeterminateCountLate = Array.from(groupboxesLate).filter(
      (cb) => cb.indeterminate
    ).length;

    if (allLate) {
      if (
        checkedCountLate === groupboxesLate.length &&
        indeterminateCountLate === 0
      ) {
        allLate.checked = true;
        allLate.indeterminate = false;
      } else if (checkedCountLate === 0 && indeterminateCountLate === 0) {
        allLate.checked = false;
        allLate.indeterminate = false;
      } else {
        allLate.checked = false;
        allLate.indeterminate = true;
      }
    }
  }

  // When 'All EARLY' or 'All LATE' is toggled, select/deselect all in a group
  toggleGroup(event) {
    const group = event.target.dataset.group;
    if (!group) return;
    const checkboxes = this.element.querySelectorAll(
      `input[type='checkbox'][data-group='${group}']`
    );
    checkboxes.forEach((cb) => {
      if (cb !== event.target) cb.checked = event.target.checked;
    });
    this.updateHiddenField();
    this.syncAllGroups();
  }

  // When an individual in a group is toggled, update the group checkbox
  syncGroup(event) {
    const group = event.target.dataset.group;
    if (!group) return;
    const allBox = this.element.querySelector(
      `input[type='checkbox'][data-group='${group}'][value='1']`
    );
    const checkboxes = this.element.querySelectorAll(
      `input[type='checkbox'][data-group='${group}']:not([value='1'])`
    );
    if (allBox) {
      allBox.checked = Array.from(checkboxes).every((cb) => cb.checked);
    }
    this.updateHiddenField();
    this.syncAllGroups();
  }

  // Debounced form submit to avoid rapid submissions
  debouncedSubmit() {
    clearTimeout(this._submitTimeout);
    // Store the currently focused checkbox (if any)
    const activeCheckbox =
      document.activeElement &&
      document.activeElement.type === "checkbox" &&
      this.element.contains(document.activeElement)
        ? document.activeElement
        : null;
    this._submitTimeout = setTimeout(() => {
      if (this.hasFormTarget) {
        this.disableAllCheckboxes();
        this.formTarget.requestSubmit();
      }
    }, 500);
  }

  // Disable all checkboxes in the dropdown (e.g., during form submit)
  disableAllCheckboxes() {
    this.element.querySelectorAll("input[type='checkbox']").forEach((cb) => {
      cb.disabled = true;
    });
  }

  // Enable all checkboxes in the dropdown (after form submit)
  enableAllCheckboxes(focusCheckbox = null) {
    // Only enable checkboxes if there are more than one menu item (i.e., not just 'All Calibrate')
    // Enable checkboxes only if there is more than one menu button
    const menuButtons = this.element.querySelectorAll(
      ".nested-dropdown__toggle.menu-button"
    );
    const shouldEnable = menuButtons.length > 1;
    this.element.querySelectorAll("input[type='checkbox']").forEach((cb) => {
      cb.disabled = !shouldEnable;
    });
    // Restore focus if possible
    if (
      focusCheckbox &&
      !focusCheckbox.disabled &&
      document.body.contains(focusCheckbox)
    ) {
      focusCheckbox.focus();
    }
  }

  // Called when All Calibrate button is clicked (select/deselect all)
  selectAllCalibrate(event) {
    event.preventDefault();
    const allCheckbox = this.element.querySelector(
      "input[type='checkbox'][name='all_calibrate']"
    );
    const allChecked = !(allCheckbox && allCheckbox.checked);
    if (allCheckbox) allCheckbox.checked = allChecked;
    // Check/uncheck all checkboxes in the dropdown
    const checkboxes = this.element.querySelectorAll("input[type='checkbox']");
    checkboxes.forEach((cb) => {
      cb.checked = allChecked;
    });
    this.updateHiddenField();
  }

  // Handles keyboard navigation for checkboxes (Escape, Enter)
  handleCheckboxKeydown(event) {
    if (event.target.type === "checkbox") {
      if (event.key === "Escape") {
        // Remove current-element class from the previously focused item
        if (event.target.classList.contains("current-element")) {
          event.target.classList.remove("current-element");
        }
        // Find the submenu ancestor
        let submenu = event.target.closest(
          "[data-nested-dropdown-target='submenu']"
        );
        if (submenu) {
          submenu.classList.add("hidden");
          // Find the menu button that opened this submenu
          // Go up to .nested-dropdown__item, then find button.nested-dropdown__toggle
          let menuItem = submenu.closest(".nested-dropdown__item");
          if (menuItem) {
            let menuButton = menuItem.querySelector(
              "button.nested-dropdown__toggle"
            );
            if (menuButton) {
              menuButton.focus();
            }
          }
        }
      } else if (event.key === "Enter") {
        // Toggle checkbox on Enter for accessibility
        event.preventDefault();
        event.target.checked = !event.target.checked;
        // Fire change event so Stimulus logic runs
        const changeEvent = new Event("change", { bubbles: true });
        event.target.dispatchEvent(changeEvent);
      }
    }
  }

  // Toggle visibility of the main dropdown menu
  toggleMain() {
    this.mainMenuTarget.classList.toggle("hidden");
  }

  // Open a submenu, ensuring only one submenu is open at a time
  openSubmenu(event) {
    this.closeAllSubmenus();
    const submenu = event.currentTarget.querySelector(
      "[data-nested-dropdown-target='submenu']"
    );
    if (submenu) {
      submenu.classList.remove("hidden");
    }
  }

  // Close a specific submenu
  closeSubmenu(event) {
    const submenu = event.currentTarget.querySelector(
      "[data-nested-dropdown-target='submenu']"
    );
    if (submenu) {
      submenu.classList.add("hidden");
    }
  }

  // Toggle a submenu open/closed, ensuring only one submenu is open at a time
  toggleSubmenu(event) {
    // Allow click, or keydown with Enter/Space/Escape
    let openedByKeyboard = false;
    if (event.type === "keydown") {
      if (event.key === "Escape") {
        // Remove current-element class from the previously focused item
        if (
          event.currentTarget.classList &&
          event.currentTarget.classList.contains("current-element")
        ) {
          event.currentTarget.classList.remove("current-element");
        }
        // Close submenu if open, keep main menu open, focus the All Calibrate button
        const submenu =
          event.currentTarget.parentElement.parentElement.querySelector(
            "[data-nested-dropdown-target='submenu']"
          );
        if (submenu && !submenu.classList.contains("hidden")) {
          submenu.classList.add("hidden");
        }
        // Focus the button that opens the menu (the All Calibrate button)
        const mainButton = this.element.querySelector(
          ".nested-dropdown__toggle[data-action*='toggleMain']"
        );
        if (mainButton) mainButton.focus();
        event.preventDefault();
        return;
      }
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      openedByKeyboard = true;
    }
    const submenu =
      event.currentTarget.parentElement.parentElement.querySelector(
        "[data-nested-dropdown-target='submenu']"
      );
    if (submenu) {
      // Always close all submenus before toggling
      this.closeAllSubmenus();
      submenu.classList.toggle("hidden");
      // If opened by keyboard, focus the first checkbox in submenu
      if (openedByKeyboard && !submenu.classList.contains("hidden")) {
        const firstCheckbox = submenu.querySelector('input[type="checkbox"]');
        if (firstCheckbox) {
          firstCheckbox.focus();
        }
      }
    }
  }

  // Close all submenus and remove hover effect from all main menu items
  closeAllSubmenus() {
    this.submenuTargets.forEach((menu) => {
      menu.classList.add("hidden");
      // Remove hover effect from all main menu items
      const menuItem = menu.closest(".nested-dropdown__item");
      if (menuItem) {
        menuItem.classList.remove("hover");
      }
    });
    // Remove 'current-element' class from any menu item
    const currentElements = this.element.querySelectorAll(".current-element");
    currentElements.forEach((el) => el.classList.remove("current-element"));
    // Blur any focused element inside the dropdown
    if (this.element.contains(document.activeElement)) {
      document.activeElement.blur();
    }
  }

  // Close menu if user clicks outside the dropdown
  handleDocumentClick(event) {
    if (!this.element.contains(event.target)) {
      this.mainMenuTarget.classList.add("hidden");
      this.closeAllSubmenus();
      // Blur any focused element inside the dropdown
      if (this.element.contains(document.activeElement)) {
        document.activeElement.blur();
      }
      // Remove 'current-element' class from any menu item
      const currentElements = this.element.querySelectorAll(".current-element");
      currentElements.forEach((el) => el.classList.remove("current-element"));
    }
  }

  // Handle checkbox change events for group logic and hidden field updates
  handleCheckboxChange(event) {
    const target = event.target;
    if (target.type !== "checkbox") return;

    // Group select logic: "All EARLY" or "All LATE"
    const allMatch =
      target.name && target.name.match(/^(.*)_(early|late)_all$/);
    if (allMatch) {
      const course = allMatch[1];
      const timing = allMatch[2];
      // Select/deselect all checkboxes for this group
      const checkboxes = this.element.querySelectorAll(
        `input[type='checkbox'][name^='${course}_${timing}_']:not([name$='_all'])`
      );
      checkboxes.forEach((cb) => {
        cb.checked = target.checked;
      });
      this.updateHiddenField();
      return;
    }

    // If an individual checkbox is changed, update the "All" checkbox for that group
    const indMatch =
      target.name && target.name.match(/^(.*)_(early|late)_\d+$/);
    if (indMatch) {
      const course = indMatch[1];
      const timing = indMatch[2];
      const allBox = this.element.querySelector(
        `input[type='checkbox'][name='${course}_${timing}_all']`
      );
      const checkboxes = this.element.querySelectorAll(
        `input[type='checkbox'][name^='${course}_${timing}_']:not([name$='_all'])`
      );
      if (allBox) {
        allBox.checked = Array.from(checkboxes).every((cb) => cb.checked);
      }
      this.updateHiddenField();
      return;
    }

    // If "All Calibrate" (top-level) is checked, check/uncheck all checkboxes
    if (target.name === "all_calibrate") {
      const allCheckboxes = this.element.querySelectorAll(
        "input[type='checkbox']"
      );
      allCheckboxes.forEach((cb) => {
        cb.checked = target.checked;
      });
      this.updateHiddenField();
      return;
    }

    this.updateHiddenField();
  }

  // Update hidden fields for selected item IDs (for form submission)
  updateHiddenField() {
    // Collect all checked item IDs from checkboxes without a name attribute (only those with a value and not group selectors)
    const checked = this.element.querySelectorAll(
      "input[type='checkbox']:checked"
    );
    const selectedIds = [];
    checked.forEach((cb) => {
      // Only collect checkboxes that have a value and no name (individual item checkboxes)
      if (!cb.name && cb.value && cb.value !== "1") {
        selectedIds.push(cb.value);
      }
    });
    // Remove any extra hidden fields first
    this.element
      .querySelectorAll("input[type='hidden'][name='selected_item_ids[]']")
      .forEach((el) => el.remove());
    this.element
      .querySelectorAll("input[type='hidden'][name='no_items_selected']")
      .forEach((el) => el.remove());
    if (selectedIds.length === 0) {
      // Add a hidden field to indicate no items selected
      let input = document.createElement("input");
      input.type = "hidden";
      input.name = "no_items_selected";
      input.value = "1";
      this.element.appendChild(input);

      // Add a hidden field with empty selected_item_ids[]
      let emptyInput = document.createElement("input");
      emptyInput.type = "hidden";
      emptyInput.name = "selected_item_ids[]";
      emptyInput.value = "";
      this.element.appendChild(emptyInput);
    } else {
      selectedIds.forEach((id) => {
        let input = document.createElement("input");
        input.type = "hidden";
        input.name = "selected_item_ids[]";
        input.value = id;
        this.element.appendChild(input);
      });
    }
  }

  // Open the main dropdown menu and first submenu (if present)
  openMain() {
    clearTimeout(this._closeMenuTimeout);
    this.mainMenuTarget.classList.remove("hidden");
  }

  // Close the main dropdown menu and all submenus (with delay)
  closeMain() {
    // Start a timer to close the menu, but cancel if mouse enters menu
    clearTimeout(this._closeMenuTimeout);
    this._closeMenuTimeout = setTimeout(() => {
      this.mainMenuTarget.classList.add("hidden");
      this.closeAllSubmenus();
    }, 120);
  }
  // Add a mouseleave handler for the menu itself to close if mouse leaves menu and not going back to button
  // Cancel menu close timer if mouse enters menu
  menuMouseenter() {
    clearTimeout(this._closeMenuTimeout);
  }

  // Start menu close timer if mouse leaves menu
  menuMouseleave() {
    // Start a timer to close the menu, but cancel if mouse enters button
    clearTimeout(this._closeMenuTimeout);
    this._closeMenuTimeout = setTimeout(() => {
      this.mainMenuTarget.classList.add("hidden");
      this.closeAllSubmenus();
    }, 120);
  }
}
